<template>
  <div class="proxies-page">
    <div class="runtime-summary" aria-label="节点运行状态汇总">
      <button
        v-for="item in summaryItems"
        :key="item.key"
        type="button"
        class="runtime-summary-item"
        :class="[
          `runtime-summary-item--${item.tone}`,
          {
            'runtime-summary-item--selected': activeSummaryKey === item.key,
            'runtime-summary-item--alert': item.key === 'missing_child' && (runtimeSummary?.missing_child || 0) > 0,
          },
        ]"
        @click="onSummaryClick(item.key)"
      >
        <span>{{ item.label }}</span>
        <a-spin v-if="summaryLoading && runtimeSummary === null" size="small" />
        <strong v-else>{{ runtimeSummary?.[item.field] ?? '—' }}</strong>
      </button>
    </div>

    <a-card class="proxy-toolbar-card" style="margin-bottom:16px">
      <div class="mobile-proxy-actions">
        <a-button type="primary" @click="openCreate">+ 新建节点</a-button>
        <a-button :loading="loading" @click="onManualRefresh">刷新</a-button>
        <a-button @click="mobileFiltersExpanded = !mobileFiltersExpanded">
          {{ mobileFiltersExpanded ? '收起筛选' : '筛选' }}
        </a-button>
      </div>
      <div class="proxy-filter-content" :class="{ 'proxy-filter-content--expanded': mobileFiltersExpanded }">
        <a-row :gutter="8" align="middle">
        <a-col>
          <a-select v-model:value="statusFilter" placeholder="状态筛选" allowClear style="width:140px" @change="onStatusFilterChange">
            <a-select-option value="all">全部</a-select-option>
            <a-select-option value="active">运行中</a-select-option>
            <a-select-option value="creating">创建中</a-select-option>
            <a-select-option value="expired">已过期</a-select-option>
            <a-select-option value="deleted">已删除</a-select-option>
          </a-select>
        </a-col>
        <a-col>
          <a-input
            v-model:value="usernameFilter"
            placeholder="搜索账号"
            allowClear
            style="width:160px"
            @pressEnter="load"
          />
        </a-col>
        <a-col>
          <a-input
            v-model:value="passwordFilter"
            placeholder="搜索密码"
            allowClear
            style="width:160px"
            @pressEnter="load"
          />
        </a-col>
        <a-col>
          <a-button @click="load">查询</a-button>
        </a-col>
        <a-col>
          <a-button @click="onManualRefresh" :loading="loading" title="立即拉取最新数据 (列表会跟随当前筛选条件)">刷新</a-button>
        </a-col>
        <a-col>
          <a-button @click="resetFilters">重置</a-button>
        </a-col>
        <a-col>
          <a-button type="primary" @click="openCreate">+ 新建节点</a-button>
        </a-col>
        <a-col>
          <a-button
            type="primary"
            ghost
            :disabled="selectedRowKeys.length === 0"
            @click="openBatchExtend"
          >批量续费 ({{ selectedRowKeys.length }})</a-button>
        </a-col>
        <a-col>
          <a-button
            :disabled="selectedRowKeys.length === 0"
            @click="openBatchHealthcheck"
          >批量检测 ({{ selectedRowKeys.length }})</a-button>
        </a-col>
        <a-col>
          <a-button
            :disabled="selectedRowKeys.length === 0"
            :loading="batchCopyLoading"
            @click="copySelectedConfigs"
          >批量复制配置 ({{ selectedRowKeys.length }})</a-button>
        </a-col>
        <a-col flex="auto" />
        <a-col>
          <a-segmented
            v-model:value="viewMode"
            :options="[
              { label: '列表', value: 'list' },
              { label: '卡片', value: 'card' },
            ]"
          />
        </a-col>
        <a-col>
          <ProxyUserPicker
            v-model="proxyUserFilter"
            :allow-quick-create="false"
            @update:model-value="onProxyUserFilterChange"
          />
        </a-col>
        <a-col>
          <a-select
            v-model:value="ownershipFilter"
            placeholder="归属状态"
            allow-clear
            style="width:130px"
            @change="onOwnershipFilterChange"
          >
            <a-select-option value="assigned">已归属</a-select-option>
            <a-select-option value="unassigned">未归属</a-select-option>
          </a-select>
        </a-col>
        </a-row>
        <a-row :gutter="8" align="middle" style="margin-top:8px">
        <a-col>
          <a-select
            v-model:value="nodeFilter"
            mode="multiple"
            placeholder="按节点筛选"
            allowClear
            style="min-width:240px"
            :options="nodeOptions"
            :filter-option="(input: string, opt: any) => (opt.label as string).includes(input)"
            @change="load"
          />
        </a-col>
        <a-col>
          <a-select
            v-model:value="regionFilter"
            mode="multiple"
            placeholder="按大区筛选"
            allowClear
            style="min-width:240px"
            :options="regionOptions"
            :filter-option="(input: string, opt: any) => (opt.label as string).includes(input)"
            @change="load"
          />
        </a-col>
        <a-col>
          <a-input
            v-model:value="noteFilter"
            placeholder="搜索备注"
            allowClear
            style="width:200px"
            @pressEnter="load"
          />
        </a-col>
        </a-row>
      </div>
    </a-card>

    <a-table
      v-if="viewMode === 'list'"
      :columns="columns"
      :dataSource="proxies"
      rowKey="proxy_id"
      :loading="loading"
      size="small"
      :row-selection="rowSelection"
      :pagination="pagination"
      @change="onTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'status'">
          <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
        </template>
        <template v-else-if="column.key === 'region_names'">
          <a-space :size="4" wrap>
            <a-tag v-for="(name, i) in proxyRegionNames(record)" :key="i" color="blue">{{ name }}</a-tag>
          </a-space>
        </template>
        <template v-else-if="column.key === 'node_name'">
          <div>{{ record.node_name || '—' }}</div>
          <div
            v-if="record.ip_public"
            style="color:#6b7280;font-size:11px;font-family:ui-monospace,Menlo,monospace"
            :title="`节点 ${record.node_name} 的公网 IP`"
          >{{ record.ip_public }}</div>
        </template>
        <template v-else-if="column.key === 'username'">
          <span style="font-family:ui-monospace,Menlo,monospace">{{ record.username }}</span>
        </template>
        <template v-else-if="column.key === 'proxy_user'">
          <div v-if="record.proxy_user_id">
            <div>{{ record.proxy_user_wechat_nickname || `#${record.proxy_user_id}` }}</div>
            <div v-if="record.proxy_user_wechat_id" style="color:#6b7280;font-size:11px">{{ record.proxy_user_wechat_id }}</div>
          </div>
          <a-tag v-else color="orange">未归属</a-tag>
        </template>
        <template v-else-if="column.key === 'note'">
          <span
            v-if="record.note"
            :title="record.note"
            style="color:#374151;display:inline-block;max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
          >{{ record.note }}</span>
          <span v-else style="color:#d1d5db">—</span>
        </template>
        <template v-else-if="column.key === 'expire_at'">
          <div>{{ formatDateTime(record.expire_at) }}</div>
          <div style="color:#6b7280;font-size:12px">{{ formatExpireRemain(record.expire_at, record.status) }}</div>
        </template>
        <template v-else-if="column.key === 'up_bps'">
          <span :style="{ fontFamily: 'ui-monospace, Menlo, monospace', color: getRate(record.proxy_id, 'up') > 100 * 1024 ? '#f5222d' : '#374151', fontWeight: 600 }">
            {{ formatRate(getRate(record.proxy_id, 'up')) }}
          </span>
        </template>
        <template v-else-if="column.key === 'down_bps'">
          <a-space :size="6">
            <span :style="{ fontFamily: 'ui-monospace, Menlo, monospace', color: isDownSaturated(record.proxy_id) ? '#f5222d' : '#374151', fontWeight: 600 }">
              {{ formatRate(getRate(record.proxy_id, 'down')) }}
            </span>
            <a-tag v-if="isDownSaturated(record.proxy_id)" color="red" style="margin:0">跑满</a-tag>
          </a-space>
        </template>
        <template v-else-if="column.key === 'child'">
          <span style="font-family:ui-monospace,Menlo,monospace;font-size:11px">
            <span v-if="record.child_pid && record.child_pid > 0">
              pid <strong>{{ record.child_pid }}</strong>
              ·
              <a-tag
                color="purple"
                style="margin:0;font-size:11px"
                :title="'Child 二进制版本 — 与 admin 当前 agent binary 不一致时说明需要重启子进程'"
              >{{ record.child_binary_version || 'unknown' }}</a-tag>
              <span
                v-if="record.child_restart_count > 0"
                style="color:#9ca3af;margin-left:4px"
                :title="`该 child 已被 supervisor 重启 ${record.child_restart_count} 次`"
              >↻{{ record.child_restart_count }}</span>
              <span
                v-if="record.child_started_at && record.child_started_at > 0"
                style="color:#6b7280;margin-left:4px"
                :title="`Child 启动时间: ${formatDateTime(new Date(record.child_started_at * 1000).toISOString())}`"
              >up {{ formatUptime(record.child_started_at) }}</span>
            </span>
            <span v-else style="color:#9ca3af">respawn 中…</span>
          </span>
        </template>
        <template v-else-if="column.key === 'action'">
          <a-space>
            <a-button type="link" size="small" @click="showDetail(record)">详情</a-button>
            <a-button
              type="link"
              size="small"
              :loading="extendingId === record.proxy_id"
              :disabled="record.status === 'deleted' || record.status === 'creating'"
              @click="openExtend(record)"
            >{{ record.status === 'expired' ? '重新激活' : '调整时间' }}</a-button>
            <a-button type="link" size="small" @click="copyRow(record)">复制配置</a-button>
            <a-button
              v-if="isCopyCreateAvailable(record.status)"
              type="link"
              size="small"
              @click="openCopyCreate(record)"
            >复制创建</a-button>
            <a-button
              type="link"
              size="small"
              :loading="healthcheckId === record.proxy_id"
              :disabled="record.status === 'deleted' || record.status === 'expired'"
              @click="onHealthcheck(record)"
            >检测</a-button>
            <a-button
              type="link"
              size="small"
              :loading="restartingId === record.proxy_id"
              :disabled="record.status === 'deleted' || record.status === 'expired'"
              @click="askRestartChild(record.proxy_id)"
            >重启进程</a-button>
            <a-button
              type="link"
              size="small"
              :disabled="record.status !== 'active'"
              @click="openTransferTime(record)"
            >转移时间</a-button>
            <a-button
              type="link"
              size="small"
              danger
              @click="askDelete(record.proxy_id)"
            >删除</a-button>
          </a-space>
        </template>
      </template>
    </a-table>

    <div v-else>
      <div v-if="loading" style="text-align:center;padding:48px"><a-spin /></div>
      <template v-else>
        <div class="proxy-grid">
          <a-card
            v-for="proxy in proxies"
            :key="proxy.proxy_id"
            hoverable
            class="proxy-grid-card"
            :body-style="{ padding: '12px' }"
            @click="showDetail(proxy)"
          >
            <div class="compact-card-head">
              <div class="compact-card-title">
                <a-tag :color="statusColor(proxy.status)" style="margin:0">{{ statusLabel(proxy.status) }}</a-tag>
                <strong>{{ proxy.username }}</strong>
                <span>#{{ proxy.proxy_id }}</span>
              </div>
              <a-tag :color="proxy.active_clients > 0 ? 'blue' : 'default'" style="margin:0">{{ proxy.active_clients || 0 }} 连接</a-tag>
            </div>

            <div class="compact-card-line" :title="`${proxy.node_name || '未绑定服务器'} · ${proxy.ip_public || '无公网 IP'}:${proxy.port}`">
              <span class="compact-label">服务器</span>{{ proxy.node_name || '未绑定服务器' }} · {{ proxy.ip_public || '无公网 IP' }}:{{ proxy.port }}
            </div>
            <div class="compact-card-line">
              <span class="compact-label">用户</span>{{ proxy.proxy_user_wechat_nickname || '未归属' }}
            </div>

            <div class="compact-tags">
              <a-tag v-for="(name, i) in proxyRegionNames(proxy)" :key="i" color="blue">{{ name }}</a-tag>
              <a-tag v-for="(name, i) in proxyGameServers(proxy)" :key="`game-${i}`" color="green">{{ name }}</a-tag>
            </div>

            <div class="compact-metrics">
              <div><span>↓</span><strong :class="{ danger: isDownSaturated(proxy.proxy_id) }">{{ formatRate(getRate(proxy.proxy_id, 'down')) }}</strong></div>
              <div><span>↑</span><strong>{{ formatRate(getRate(proxy.proxy_id, 'up')) }}</strong></div>
              <div class="compact-expire"><span>到期</span><strong>{{ formatExpireRemain(proxy.expire_at, proxy.status) || formatDateTime(proxy.expire_at) }}</strong></div>
            </div>

            <div class="compact-actions" @click.stop>
              <a-button size="small" type="primary" ghost @click="showDetail(proxy)">详情</a-button>
              <a-button size="small" @click="copyRow(proxy)">复制</a-button>
              <a-button size="small" :disabled="proxy.status === 'deleted' || proxy.status === 'creating'" :loading="extendingId === proxy.proxy_id" @click="openExtend(proxy)">{{ proxy.status === 'expired' ? '激活' : '调整时间' }}</a-button>
              <a-dropdown trigger="click">
                <a-button size="small">更多</a-button>
                <template #overlay>
                  <a-menu>
                    <a-menu-item v-if="isCopyCreateAvailable(proxy.status)" @click="openCopyCreate(proxy)">复制创建</a-menu-item>
                    <a-menu-item :disabled="proxy.status === 'deleted' || proxy.status === 'expired'" @click="onHealthcheck(proxy)">检测</a-menu-item>
                    <a-menu-item :disabled="proxy.status === 'deleted' || proxy.status === 'expired'" @click="askRestartChild(proxy.proxy_id)">重启进程</a-menu-item>
                    <a-menu-item :disabled="proxy.status !== 'active'" @click="openTransferTime(proxy)">转移剩余时间</a-menu-item>
                    <a-menu-divider />
                    <a-menu-item danger @click="askDelete(proxy.proxy_id)">删除</a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </div>
          </a-card>
        </div>
        <a-empty v-if="proxies.length === 0" description="暂无节点" style="padding:48px 0" />
      </template>
    </div>

    <ProxyDetailDrawer v-model:visible="drawerVisible" :proxy="selectedProxy" @updated="load" />
    <CreateProxyModal
      v-model:visible="createVisible"
      :copy-source="copySource"
      @created="onCreated"
    />
    <ExtendProxyModal
      v-model:visible="extendModalOpen"
      :proxies="extendTarget ? [extendTarget] : []"
      :proxy-id="extendTarget?.proxy_id ?? null"
      @success="onExtendSuccess"
    />
    <BatchExtendModal
      v-model:visible="batchExtendOpen"
      :proxy-ids="selectedRowKeys"
      @success="onBatchExtendSuccess"
    />
    <HealthcheckProgressModal
      :visible="healthcheckModalOpen"
      :title="healthcheckTitle"
      :loading="healthcheckId !== null"
      :result="healthcheckResult"
      @close="healthcheckModalOpen = false"
    />
    <BatchHealthcheckModal
      v-model:visible="batchHealthcheckOpen"
      :proxy-ids="selectedRowKeys"
    />
    <TransferProxyTimeModal
      v-model:visible="transferModalOpen"
      :source="transferSource"
      @success="onTransferTimeSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { useRoute, useRouter } from 'vue-router';
import { listProxies, getProxyRuntimeSummary, deleteProxy, restartChild, healthcheckProxy, extendProxy, batchExtendProxy, type TransferProxyTimeResponse } from '../api/proxies';
import { confirmOperation, PROXY_DELETE_HINT, PROXY_RESTART_CHILD_HINT } from '../utils/operationHints';
import { listNodes, resolveNodeAddress } from '../api/nodes';
import { listRegions } from '../api/regions';
import ProxyDetailDrawer from '../components/ProxyDetailDrawer.vue';
import CreateProxyModal from '../components/CreateProxyModal.vue';
import ProxyUserPicker from '../components/ProxyUserPicker.vue';
import ExtendProxyModal from '../components/ExtendProxyModal.vue';
import BatchExtendModal from '../components/BatchExtendModal.vue';
import BatchHealthcheckModal from '../components/BatchHealthcheckModal.vue';
import TransferProxyTimeModal from '../components/TransferProxyTimeModal.vue';
import { isCopyCreateAvailable } from '../components/proxyCopyCreate';
import { buildBatchProxyConfigText, buildProxyConfigLine } from '../components/proxyConfigCopy';
import HealthcheckProgressModal from '../components/HealthcheckProgressModal.vue';
import type { HealthcheckResult } from '../api/proxies';
import type { ProxyRuntimeSummary } from '../api/proxies';
import type { ProxyItem } from '../types';
import { formatTrafficBytes, formatTrafficRate } from '../utils/trafficFormat';

const route = useRoute();
const router = useRouter();

const proxies = ref<ProxyItem[]>([]);
const loading = ref(false);
const runtimeSummary = ref<ProxyRuntimeSummary | null>(null);
const summaryLoading = ref(false);
const viewMode = ref<'list' | 'card'>('card');
const mobileFiltersExpanded = ref(false);
const proxyDownSaturatedBps = 1048576;
let loadInFlight = false;
// 渲染用的最新速率 (bytes/sec)。
const rateMap = ref<Record<number, { up: number; down: number }>>({});
// Per-row loading state for the "重启子进程" button so the click
// target keeps its own spinner rather than blocking the whole table.
const restartingId = ref<number | null>(null);
// Per-row loading state for the "检测" button so the click target
// keeps its own spinner. Same pattern as restartingId.
const healthcheckId = ref<number | null>(null);
const healthcheckModalOpen = ref(false);
const healthcheckTitle = ref('');
const healthcheckResult = ref<HealthcheckResult | null>(null);
// Per-row loading state for the "调整时间" button. Mirrors restartingId
// so each row's spinner is independent.
const extendingId = ref<number | null>(null);
const transferModalOpen = ref(false);
const transferSource = ref<ProxyItem | null>(null);
const statusFilter = ref<string | undefined>('active');
const runtimeStateFilter = ref<'healthy' | 'missing_child' | 'connected' | undefined>();
const usernameFilter = ref('');
const passwordFilter = ref('');
const nodeFilter = ref<number[]>([]);
const regionFilter = ref<number[]>([]);
const noteFilter = ref('');
const proxyUserFilter = ref<number | null>(null);
const ownershipFilter = ref<'assigned' | 'unassigned' | undefined>();
// Pre-computed options for the multi-select filters. Populated by
// loadFilterOptions() which runs once on mount (data rarely changes
// — the operator creates nodes/regions infrequently).
const nodeOptions = ref<{ label: string; value: number }[]>([]);
const regionOptions = ref<{ label: string; value: number }[]>([]);

type SummaryKey = 'all' | 'connected' | 'healthy' | 'missing_child' | 'creating' | 'expired' | 'deleted';
type SummaryTone = 'neutral' | 'success' | 'danger' | 'info' | 'warning';
const summaryItems: Array<{
  key: SummaryKey;
  field: keyof ProxyRuntimeSummary;
  label: string;
  tone: SummaryTone;
}> = [
  { key: 'all', field: 'total', label: '全部', tone: 'neutral' },
  { key: 'connected', field: 'active_connections', label: '实时连接', tone: 'info' },
  { key: 'healthy', field: 'healthy', label: '正常运行', tone: 'success' },
  { key: 'missing_child', field: 'missing_child', label: '运行中无子进程', tone: 'danger' },
  { key: 'creating', field: 'creating', label: '创建中', tone: 'info' },
  { key: 'expired', field: 'expired', label: '已过期（已停止）', tone: 'warning' },
  { key: 'deleted', field: 'deleted', label: '已删除', tone: 'neutral' },
];

const activeSummaryKey = computed<SummaryKey | null>(() => {
  if (runtimeStateFilter.value) return runtimeStateFilter.value;
  if (statusFilter.value === 'all') return 'all';
  if (statusFilter.value === 'creating' || statusFilter.value === 'expired' || statusFilter.value === 'deleted') {
    return statusFilter.value;
  }
  return null;
});

// 手动刷新按钮：忽略筛选状态是否为空，强制重新拉取 (跟「查询」行为一致)。
// 让操作员在筛选状态下也能主动刷新而不必先清筛选。
const onManualRefresh = () => {
  load();
};

const columns = [
  { title: 'ID', dataIndex: 'proxy_id', key: 'proxy_id', width: 60 },
  { title: '大区', key: 'region_names', width: 160 },
  // Node column renders name + IP (the player-facing 5-field config
  // includes the IP, so showing it here makes it easy to grab the
  // right one for a given NAT).
  { title: '节点', key: 'node_name', width: 140 },
  { title: '所属用户', key: 'proxy_user', width: 150 },
  { title: '端口', dataIndex: 'port', key: 'port', width: 60 },
  { title: '节点账号', dataIndex: 'username', key: 'username', width: 100 },
  { title: '备注', dataIndex: 'note', key: 'note', width: 240 },
  { title: '连接数', dataIndex: 'active_clients', key: 'active_clients', width: 90, customRender: (v: any) => String(v.record?.active_clients || 0) },
  // 上行/下行 显示实时速度 (5s 差值算 bps)，下行红色阈值 1MB/s，上行 100KB/s，
  // 让操作员一眼能看出哪个代理正在占用大带宽。
  { title: '上行速度', key: 'up_bps', width: 100, customRender: (v: any) => formatRate(getRate(v.record?.proxy_id, 'up')) },
  { title: '下行速度', key: 'down_bps', width: 100, customRender: (v: any) => formatRate(getRate(v.record?.proxy_id, 'down')) },
  { title: '累计↓', key: 'bytes_down', width: 80, customRender: (v: any) => formatBytes(v.record?.bytes_down || 0) },
  { title: '状态', dataIndex: 'status', key: 'status', width: 80 },
  { title: '到期', dataIndex: 'expire_at', key: 'expire_at', width: 180 },
  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160, customRender: (v: any) => formatDateTime(v.record?.created_at) },
  { title: '子进程', key: 'child', width: 200 },
  { title: '操作', key: 'action', width: 200, fixed: 'right' as const },
];

const formatBytes = formatTrafficBytes;

// 实时速率格式化。<1 KB/s 直接 B/s，避免单玩家慢线时显示 0.00 MB/s。
const formatRate = formatTrafficRate;

// Per-row rate lookup. `dir` = 'up' | 'down'. Used by a-table's
// customRender callback.
const getRate = (proxyId: number, dir: 'up' | 'down'): number => {
  const r = rateMap.value[proxyId];
  if (!r) return 0;
  return dir === 'up' ? r.up : r.down;
};

const isDownSaturated = (proxyId: number): boolean => getRate(proxyId, 'down') > proxyDownSaturatedBps;

const proxyRegionNames = (p: ProxyItem): string[] => {
  const names = p.region_names && p.region_names.length > 0
    ? p.region_names
    : (p.region_name ? [p.region_name] : []);
  return names.length > 0 ? names : ['未设置大区'];
};

const proxyGameServers = (p: ProxyItem): string[] => {
  const names = p.game_servers || [];
  return names
    .map((name) => name.trim())
    .filter((name, index, arr) => name && arr.indexOf(name) === index);
};

// Backend returns ISO 8601 with offset, e.g. "2026-09-24T17:33:08+08:00".
// Drop the timezone offset for a compact operator-friendly display.
const formatDateTime = (s?: string) => {
  if (!s) return '';
  return s.replace('T', ' ').replace(/\+\d{2}:\d{2}$|Z$/, '');
};

const formatExpireRemain = (expireAt?: string, status?: string) => {
  if (!expireAt) return '';
  if (status === 'expired') return '(已过期)';
  const t = new Date(expireAt).getTime();
  if (Number.isNaN(t)) return '';
  const diff = t - Date.now();
  if (diff <= 0) return '(已过期)';
  const totalHours = Math.floor(diff / 3600000);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `(${days}天${hours}小时)`;
};

// Format a child uptime in a compact "up Xd Yh Zm" or "up Yh Zm" or
// "up Zm" form. Returns "—" when the timestamp is missing or in the
// future (clock skew).
const formatUptime = (startedAt?: number | null) => {
  if (!startedAt || startedAt <= 0) return '—';
  const now = Math.floor(Date.now() / 1000);
  const secs = now - startedAt;
  if (secs < 0) return '—';
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return remM === 0 ? `${h}h` : `${h}h${remM}m`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH === 0 ? `${d}d` : `${d}d${remH}h`;
};

const statusColor = (s: string) => {
  switch (s) {
    case 'active': return 'green';
    case 'creating': return 'blue';
    case 'expired': return 'orange';
    case 'deleted': return 'red';
    default: return 'default';
  }
};

const statusLabel = (s: string) => {
  switch (s) {
    case 'active': return '运行中';
    case 'creating': return '创建中';
    case 'expired': return '已过期';
    case 'deleted': return '已删除';
    default: return s;
  }
};

const drawerVisible = ref(false);
const selectedProxy = ref<ProxyItem | null>(null);
const createVisible = ref(false);
const copySource = ref<ProxyItem | null>(null);
// Single shared ExtendProxyModal instance, fed by clicking any row's
// 调整时间 button. Keeps modal state isolated from the list.
const extendModalOpen = ref(false);
const extendTarget = ref<ProxyItem | null>(null);
// Selected rows for batch extend. a-table's row-selection binds to
// this list — we drive the "select all on this page" UX from it.
const selectedRowKeys = ref<number[]>([]);
const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  onChange: (keys: (string | number)[]) => {
    selectedRowKeys.value = keys.map((k) => Number(k));
  },
  // Keep selections across page flips so the operator can
  // multi-page-select without losing context. Combined with
  // a-table's preserveSelectedRowKeys (default true).
  preserveSelectedRowKeys: true,
}));

// Controlled a-table pagination. We bind this rather than passing
// an inline object so the @change handler can react to pageSize /
// pageNumber changes and trigger a fresh load(). Without this,
// switching from 30 → 100 just re-renders the existing data set
// and the operator wonders why the new page size "didn't refresh".
const pagination = ref({
  current: 1,
  pageSize: 30,
  showTotal: (total: number) => `共 ${total} 条`,
  showSizeChanger: true,
  pageSizeOptions: ['30', '50', '100'],
  total: 0,
});
const onTableChange = (pag: { current: number; pageSize: number }) => {
  // Update local state so the table re-renders, then re-issue the
  // load. The backend's listProxies doesn't currently accept page/
  // pageSize (it returns the full filtered set, capped server-side),
  // so the "refresh" is really about re-running the same query so
  // the row count stays current after the page change.
  pagination.value = { ...pagination.value, current: pag.current, pageSize: pag.pageSize };
  load();
};
// Batch extend modal: a-table sends proxy IDs in the order they
// appear in the table. The modal calls batchExtendProxy and
// surfaces a per-row result summary.
const batchExtendOpen = ref(false);
const batchHealthcheckOpen = ref(false);
const batchCopyLoading = ref(false);
const copySelectedConfigs = async () => {
  if (selectedRowKeys.value.length === 0) {
    message.warning('请先勾选要复制的节点');
    return;
  }

  const proxiesByID = new Map(proxies.value.map((proxy) => [proxy.proxy_id, proxy]));
  const selected = selectedRowKeys.value
    .map((proxyID) => proxiesByID.get(proxyID))
    .filter((proxy): proxy is ProxyItem => Boolean(proxy));
  if (selected.length !== selectedRowKeys.value.length) {
    message.error('部分选中的节点已不在当前列表，请刷新后重新选择');
    return;
  }

  batchCopyLoading.value = true;
  try {
    const nodeIDs = [...new Set(selected.map((proxy) => proxy.node_id))];
    const results = await Promise.allSettled(nodeIDs.map(async (nodeID) => {
      const response = await resolveNodeAddress(nodeID);
      return [nodeID, response.data.resolved_ip] as const;
    }));
    const resolvedIPs = new Map<number, string>();
    const failedNodes: string[] = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value[1]) {
        resolvedIPs.set(result.value[0], result.value[1]);
        return;
      }
      const nodeID = nodeIDs[index];
      const nodeName = selected.find((proxy) => proxy.node_id === nodeID)?.node_name;
      failedNodes.push(nodeName || `节点 ${nodeID}`);
    });
    if (failedNodes.length > 0) {
      message.error(`${failedNodes.join('、')} 地址解析失败，未复制任何配置`);
      return;
    }

    await navigator.clipboard.writeText(buildBatchProxyConfigText(selected, resolvedIPs));
    message.success(`已复制 ${selected.length} 条配置，每条一行`);
  } catch (e: any) {
    message.error(e?.message || '批量复制失败');
  } finally {
    batchCopyLoading.value = false;
  }
};
const openBatchHealthcheck = () => {
  if (selectedRowKeys.value.length === 0) {
    message.warning('请先勾选要检测的节点');
    return;
  }
  batchHealthcheckOpen.value = true;
};
const openBatchExtend = () => {
  if (selectedRowKeys.value.length === 0) {
    message.warning('请先勾选要续费的节点');
    return;
  }
  batchExtendOpen.value = true;
};
const onBatchExtendSuccess = (payload: { updated: number; skipped: number; ws_failed: number; db_errors: number }) => {
  // Show a summary toast — the modal itself shows the per-row
  // table, so we keep this short.
  const parts: string[] = [`已续费 ${payload.updated}`];
  if (payload.skipped) parts.push(`跳过 ${payload.skipped}`);
  if (payload.ws_failed) parts.push(`agent 离线 ${payload.ws_failed}`);
  if (payload.db_errors) parts.push(`DB 错误 ${payload.db_errors}`);
  message.success(parts.join('，'));
  // Refresh the table so updated expire_at values show up.
  load();
  // Clear the selection — the operator has acted on these rows.
  selectedRowKeys.value = [];
  batchExtendOpen.value = false;
};

const buildScopeParams = (): Record<string, any> => {
  const params: Record<string, any> = {};
  if (usernameFilter.value.trim()) params.username = usernameFilter.value.trim();
  if (passwordFilter.value.trim()) params.password = passwordFilter.value.trim();
  if (noteFilter.value.trim()) params.note = noteFilter.value.trim();
  if (nodeFilter.value.length) params.node_ids = [...nodeFilter.value];
  if (regionFilter.value.length) params.region_ids = [...regionFilter.value];
  if (proxyUserFilter.value !== null) params.proxy_user_id = proxyUserFilter.value;
  if (ownershipFilter.value) params.ownership = ownershipFilter.value;
  return params;
};

const buildListParams = (): Record<string, any> => {
  const params = buildScopeParams();
  if (runtimeStateFilter.value) {
    params.runtime_state = runtimeStateFilter.value;
  } else if (statusFilter.value) {
    params.status = statusFilter.value;
  }
  return params;
};

const loadSummary = async () => {
  summaryLoading.value = true;
  try {
    const r = await getProxyRuntimeSummary(buildScopeParams());
    runtimeSummary.value = r.data;
  } catch {
    // Keep the last valid summary visible. A summary failure must not
    // block the proxy list or any operator action.
  } finally {
    summaryLoading.value = false;
  }
};

const load = async () => {
  if (loadInFlight) return;
  loadInFlight = true;
  loading.value = true;
  const summaryRequest = loadSummary();
  try {
    const params = buildListParams();
    const r = await listProxies(params);
    const next: ProxyItem[] = r.data || [];
    const nextRates: Record<number, { up: number; down: number }> = {};
    for (const p of next) {
      nextRates[p.proxy_id] = { up: Number(p.up_bps || 0), down: Number(p.down_bps || 0) };
    }
    proxies.value = next;
    if (selectedProxy.value) {
      const refreshed = next.find((p) => p.proxy_id === selectedProxy.value?.proxy_id);
      if (refreshed) selectedProxy.value = refreshed;
    }
    rateMap.value = nextRates;
  } finally {
    await summaryRequest;
    loading.value = false;
    loadInFlight = false;
  }
};

// Populate node/region multi-select options once. Cheap (a couple
// of rows) so no need to refresh on every proxy load.
const loadFilterOptions = async () => {
  try {
    const [nodesR, regionsR] = await Promise.all([
      listNodes(),
      listRegions(),
    ]);
    nodeOptions.value = (nodesR.data || []).map((n: any) => ({
      label: `${n.name} (#${n.id})`,
      value: n.id,
    }));
    regionOptions.value = (regionsR.data || []).map((r: any) => ({
      label: r.name,
      value: r.id,
    }));
  } catch {
    // Filters just show no options; load() still works.
  }
};

const onSummaryClick = (key: SummaryKey) => {
  const clearToAll = key === 'all' || activeSummaryKey.value === key;
  runtimeStateFilter.value = undefined;
  if (clearToAll) {
    statusFilter.value = 'all';
  } else if (key === 'healthy' || key === 'missing_child' || key === 'connected') {
    statusFilter.value = undefined;
    runtimeStateFilter.value = key;
  } else {
    statusFilter.value = key;
  }
  pagination.value.current = 1;
  load();
};

const onStatusFilterChange = () => {
  runtimeStateFilter.value = undefined;
  pagination.value.current = 1;
  load();
};

const onOwnershipFilterChange = () => {
  if (ownershipFilter.value === 'unassigned') proxyUserFilter.value = null;
  pagination.value.current = 1;
  selectedRowKeys.value = [];
  load();
};

const onProxyUserFilterChange = (proxyUserID: number | null) => {
  if (proxyUserID !== null) ownershipFilter.value = 'assigned';
  pagination.value.current = 1;
  selectedRowKeys.value = [];
  load();
};

const resetFilters = () => {
  statusFilter.value = undefined;
  runtimeStateFilter.value = undefined;
  usernameFilter.value = '';
  passwordFilter.value = '';
  nodeFilter.value = [];
  regionFilter.value = [];
  noteFilter.value = '';
  proxyUserFilter.value = null;
  ownershipFilter.value = undefined;
  pagination.value.current = 1;
  selectedRowKeys.value = [];
  load();
};

const showDetail = (p: ProxyItem) => {
  selectedProxy.value = p;
  drawerVisible.value = true;
};

const openCreate = () => {
  copySource.value = null;
  createVisible.value = true;
};

watch(
  () => route.query.create,
  async (create) => {
    if (create !== '1') return;
    openCreate();
    const query = { ...route.query };
    delete query.create;
    await router.replace({ path: route.path, query });
  },
  { immediate: true },
);

const openCopyCreate = (proxy: ProxyItem) => {
  copySource.value = proxy;
  createVisible.value = true;
};

const openExtend = (p: ProxyItem) => {
  extendTarget.value = p;
  extendModalOpen.value = true;
};

const openTransferTime = (proxy: ProxyItem) => {
  transferSource.value = proxy;
  transferModalOpen.value = true;
};

const onTransferTimeSuccess = (payload: TransferProxyTimeResponse) => {
  const text = `剩余时间已转移到节点 #${payload.target_proxy_id}，源节点已删除`;
  if (payload.runtime_sync_warnings?.length) {
    message.warning(`${text}，运行态将在 Agent 重连后自动收敛`);
  } else {
    message.success(text);
  }
  transferModalOpen.value = false;
  transferSource.value = null;
  void load();
};

const onExtendSuccess = (payload: { proxy_id: number; expire_at: string; reactivated: boolean; adjustment: 'extend' | 'reduce'; runtime_sync_pending?: boolean }) => {
  const p = proxies.value.find((x) => x.proxy_id === payload.proxy_id);
  if (p) {
    p.expire_at = payload.expire_at;
    if (payload.reactivated) p.status = 'active';
  }
  extendModalOpen.value = false;
  const actionText = payload.reactivated ? '已重新激活，到期时间' : payload.adjustment === 'reduce' ? '已减少时间，到期时间' : '已续费到';
  if (payload.runtime_sync_pending) {
    message.warning(`${actionText} ${payload.expire_at.slice(0, 10)}，已保存，等待节点同步，请勿重复扣减`);
  } else {
    message.success(`${actionText} ${payload.expire_at.slice(0, 10)}`);
  }
  if (payload.reactivated) void load();
};

// Mark the row as in-flight while the modal is open so its spinner
// stays in sync. The modal's own submitting state covers the actual
// request; this is a UX hint to disable the button.
const extendingIdSync = computed(() => (extendModalOpen.value && extendTarget.value ? extendTarget.value.proxy_id : null));
// Keep extendingId reactive with the computed so the button spinner
// reflects modal activity.
watch(extendingIdSync, (v) => { extendingId.value = v; });

const onCreated = () => {
  // Reload after a brief delay so the new proxy is visible in the table.
  setTimeout(load, 200);
};

const askDelete = (id: number) => {
  confirmOperation(PROXY_DELETE_HINT, () => onDelete(id));
};

const askRestartChild = (id: number) => {
  confirmOperation(PROXY_RESTART_CHILD_HINT, () => onRestartChild(id));
};

const onDelete = async (id: number) => {
  try {
    await deleteProxy(id);
    message.success('已删除');
    load();
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '删除失败');
  }
};

const onRestartChild = async (id: number) => {
  restartingId.value = id;
  try {
    const r = await restartChild(id);
    const acked = r.data?.acked;
    message.success(
      acked
        ? '已发送重启命令, agent 已 ack; child 会在 5-15 秒内被 respawn'
        : '已发送重启命令,等待 agent ack'
    );
    // The child respawn takes a few seconds; give it a moment then
    // refresh the table so the operator sees the new PID / version.
    setTimeout(() => load(), 3000);
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '重启失败');
  } finally {
    restartingId.value = null;
  }
};

const onHealthcheck = async (record: ProxyItem) => {
  healthcheckId.value = record.proxy_id;
  healthcheckResult.value = null;
  healthcheckTitle.value = `节点检测 — 端口 ${record.port}`;
  healthcheckModalOpen.value = true;
  try {
    const r = await healthcheckProxy(record.proxy_id);
    healthcheckResult.value = r.data;
  } catch (e: any) {
    healthcheckModalOpen.value = false;
    message.error(e.response?.data?.error?.message || '检测失败');
  } finally {
    healthcheckId.value = null;
  }
};

const copyRow = async (p: ProxyItem) => {
  // Build the 5-field "IP|端口|账号|密码|到期" string the player expects.
  let ip = '';
  try {
    const r = await resolveNodeAddress(p.node_id);
    ip = r.data.resolved_ip;
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '节点域名解析失败，未复制配置');
    return;
  }
  try {
    const joined = buildProxyConfigLine(p, ip);
    await navigator.clipboard.writeText(joined);
    message.success('已复制 5 字段');
  } catch {
    message.error('复制失败');
  }
};

onMounted(() => {
  const routeStatus = typeof route.query.status === 'string' ? route.query.status : '';
  if (routeStatus) statusFilter.value = routeStatus;
  const routeOwnership = typeof route.query.ownership === 'string' ? route.query.ownership : '';
  if (routeOwnership === 'assigned' || routeOwnership === 'unassigned') {
    ownershipFilter.value = routeOwnership;
  }
  const routeProxyUserID = Number(route.query.proxy_user_id || 0);
  if (routeProxyUserID > 0) proxyUserFilter.value = routeProxyUserID;
  load();
  loadFilterOptions();
});
</script>

<style scoped>
.runtime-summary {
  display: grid;
  grid-template-columns: repeat(7, minmax(132px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  overflow-x: auto;
}

.runtime-summary-item {
  min-width: 132px;
  min-height: 62px;
  padding: 9px 11px;
  border: 1px solid #d9dee7;
  border-radius: 6px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.runtime-summary-item:hover,
.runtime-summary-item--selected {
  border-color: #1677ff;
  background: #f0f7ff;
}

.runtime-summary-item span {
  display: block;
  min-height: 32px;
  font-size: 12px;
  line-height: 16px;
}

.runtime-summary-item strong {
  display: block;
  margin-top: 2px;
  color: #111827;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 20px;
  line-height: 20px;
}

.runtime-summary-item--success strong { color: #15803d; }
.runtime-summary-item--info strong { color: #1677ff; }
.runtime-summary-item--warning strong { color: #c2410c; }
.runtime-summary-item--alert {
  border-color: #fca5a5;
  background: #fff7f7;
}
.runtime-summary-item--alert strong { color: #dc2626; }

.mobile-proxy-actions {
  display: none;
}

@media (max-width: 900px) {
  .runtime-summary {
    grid-template-columns: repeat(3, minmax(132px, 1fr));
    overflow-x: visible;
  }
}

@media (max-width: 520px) {
  .runtime-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .runtime-summary-item {
    min-width: 0;
  }

  .mobile-proxy-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.55fr) minmax(0, 0.55fr);
    gap: 8px;
  }

  .proxy-filter-content {
    display: none;
    margin-top: 10px;
  }

  .proxy-filter-content--expanded {
    display: block;
  }

  .proxy-toolbar-card :deep(.ant-row) {
    flex-direction: column;
    align-items: stretch !important;
  }
  .proxy-toolbar-card :deep(.ant-col) {
    width: 100%;
    max-width: 100%;
  }
  .proxy-toolbar-card :deep(.ant-select),
  .proxy-toolbar-card :deep(.ant-input),
  .proxy-toolbar-card :deep(.ant-input-affix-wrapper),
  .proxy-toolbar-card :deep(.ant-btn),
  .proxy-toolbar-card :deep(.ant-segmented) {
    width: 100% !important;
    min-width: 0 !important;
  }
  :deep(.ant-empty-image),
  :deep(.ant-empty-image svg) {
    width: 100% !important;
    max-width: 100% !important;
  }

  .proxy-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.proxy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 10px;
}

.proxy-grid-card {
  border-radius: 10px;
}

.compact-card-head,
.compact-card-title,
.compact-actions,
.compact-metrics {
  display: flex;
  align-items: center;
}
.compact-card-head { justify-content: space-between; gap: 8px; }
.compact-card-title { min-width: 0; gap: 7px; }
.compact-card-title strong {
  overflow: hidden;
  color: #172033;
  font-family: ui-monospace, Menlo, monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.compact-card-title span { color: #9ca3af; font: 11px ui-monospace, Menlo, monospace; }
.compact-card-line {
  overflow: hidden;
  margin-top: 7px;
  color: #596579;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.compact-label { display: inline-block; width: 42px; color: #9ca3af; }
.compact-tags {
  display: flex;
  overflow: hidden;
  gap: 4px;
  height: 23px;
  margin-top: 9px;
}
.compact-tags :deep(.ant-tag) { flex: 0 0 auto; margin: 0; }
.compact-metrics {
  gap: 6px;
  margin-top: 10px;
}
.compact-metrics > div {
  min-width: 0;
  flex: 1;
  border: 1px solid #eef2f7;
  border-radius: 6px;
  padding: 6px 7px;
  background: #f8fafc;
}
.compact-metrics span { margin-right: 4px; color: #9ca3af; font-size: 11px; }
.compact-metrics strong {
  color: #253044;
  font: 600 12px ui-monospace, Menlo, monospace;
  white-space: nowrap;
}
.compact-metrics strong.danger { color: #dc2626; }
.compact-expire { flex: 1.45 !important; }
.compact-expire strong {
  display: inline-block;
  max-width: calc(100% - 30px);
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
.compact-actions { justify-content: flex-end; gap: 6px; margin-top: 10px; padding-top: 9px; border-top: 1px solid #f0f2f5; }
</style>
